import * as React from 'react';

/**
 * Keeps one "active" indicator (sidebar pill, tab pill) under whichever child
 * of `ref` matches `selector`. It only publishes geometry as CSS custom
 * properties on the container (`--ind-x/-y/-w/-h`, `--ind-on`); the indicator
 * element and its slide (`--dur-2`, `--ease-out`) are CSS in kit-next.css.
 *
 * - `data-ind` is set on the container as soon as the first position is known
 *   (before paint), so the item's own highlight can hand over to the indicator
 *   with no flash.
 * - `data-ind-ready` follows one frame later and turns the slide on, so the
 *   first position is never animated from the corner.
 * - While a collapsible section animates, positions below it move every frame,
 *   so the indicator tracks them without easing (`data-follow`).
 *
 * Measured from the DOM, so the markup of the items stays as it was.
 */
export function useActiveIndicator(ref: React.RefObject<HTMLElement | null>, selector: string) {
  React.useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    let frame = 0;
    let follow = 0;
    let followFrame = 0;

    const measure = () => {
      const active = root.querySelector<HTMLElement>(selector);
      // An item inside a folded section is not on screen: no indicator.
      if (!active || active.closest('[data-slot="collapse"][data-state="closed"]')) {
        root.style.setProperty('--ind-on', '0');
        return;
      }
      const box = root.getBoundingClientRect();
      const item = active.getBoundingClientRect();
      // `scroll*`: the indicator lives in the scrolled content of the container.
      root.style.setProperty('--ind-x', `${item.left - box.left - root.clientLeft + root.scrollLeft}px`);
      root.style.setProperty('--ind-y', `${item.top - box.top - root.clientTop + root.scrollTop}px`);
      root.style.setProperty('--ind-w', `${item.width}px`);
      root.style.setProperty('--ind-h', `${item.height}px`);
      root.style.setProperty('--ind-on', '1');
      root.setAttribute('data-ind', '');
    };
    const schedule = () => {
      if (!frame) {
        frame = requestAnimationFrame(() => {
          frame = 0;
          measure();
        });
      }
    };

    // Collapsing/expanding sections shift the items below them for a while.
    const isFold = (event: Event) => event.target instanceof HTMLElement && event.target.dataset.slot === 'collapse';
    const trackFrame = () => {
      measure();
      followFrame = follow > 0 ? requestAnimationFrame(trackFrame) : 0;
    };
    const foldStart = (event: Event) => {
      if (!isFold(event)) return;
      follow += 1;
      root.setAttribute('data-follow', '');
      if (!followFrame) followFrame = requestAnimationFrame(trackFrame);
    };
    const foldEnd = (event: Event) => {
      if (!isFold(event)) return;
      follow = Math.max(0, follow - 1);
      if (follow === 0) {
        root.removeAttribute('data-follow');
        measure();
      }
    };

    measure();
    const ready = requestAnimationFrame(() => root.setAttribute('data-ind-ready', ''));
    const mutations = new MutationObserver(schedule);
    mutations.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-current', 'data-active', 'data-state'],
    });
    const resize = new ResizeObserver(schedule);
    resize.observe(root);
    root.addEventListener('transitionrun', foldStart);
    root.addEventListener('transitionend', foldEnd);
    root.addEventListener('transitioncancel', foldEnd);
    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(ready);
      cancelAnimationFrame(followFrame);
      mutations.disconnect();
      resize.disconnect();
      root.removeEventListener('transitionrun', foldStart);
      root.removeEventListener('transitionend', foldEnd);
      root.removeEventListener('transitioncancel', foldEnd);
      root.removeAttribute('data-ind');
      root.removeAttribute('data-ind-ready');
      root.removeAttribute('data-follow');
    };
  }, [ref, selector]);
}
